import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useOktaAuth } from '@okta/okta-react';
import { useSurveyRuntimeApi } from './SurveyRuntimeApiContext';
import { getApiErrorMessage } from '../api/runtimeErrors';

const SurveyRuntimeAuthContext = createContext(null);

export function SurveyRuntimeAuthProvider({ children }) {
  const { authState } = useOktaAuth();
  const { apiFetch } = useSurveyRuntimeApi();
  const [me, setMe] = useState(null);
  const [meLoading, setMeLoading] = useState(false);
  const [error, setError] = useState(null);

  const isAuthenticated = authState?.isAuthenticated === true;
  const isPending = authState == null || authState.isPending === true;

  const refreshMe = useCallback(async () => {
    if (!isAuthenticated) {
      setMe(null);
      return;
    }
    setMeLoading(true);
    setError(null);
    try {
      const m = await apiFetch('/auth/me');
      setMe(m);
    } catch (e) {
      setMe(null);
      setError(getApiErrorMessage(e, 'Session expired'));
    } finally {
      setMeLoading(false);
    }
  }, [apiFetch, isAuthenticated]);

  useEffect(() => {
    if (isPending) return;
    if (isAuthenticated) {
      void refreshMe();
    } else {
      setMe(null);
      setError(null);
    }
  }, [isAuthenticated, isPending, refreshMe]);

  const hasPermission = useCallback(
    (...codes) => {
      const p = new Set(me?.permissions ?? []);
      return codes.every((c) => p.has(c));
    },
    [me],
  );

  const hasAnyPermission = useCallback(
    (...codes) => {
      const p = new Set(me?.permissions ?? []);
      return codes.some((c) => p.has(c));
    },
    [me],
  );

  const value = useMemo(
    () => ({
      isAuthenticated,
      isPending,
      me,
      meLoading,
      error,
      refreshMe,
      hasPermission,
      hasAnyPermission,
    }),
    [
      isAuthenticated,
      isPending,
      me,
      meLoading,
      error,
      refreshMe,
      hasPermission,
      hasAnyPermission,
    ],
  );

  return (
    <SurveyRuntimeAuthContext.Provider value={value}>
      {children}
    </SurveyRuntimeAuthContext.Provider>
  );
}

export function useSurveyRuntimeAuth() {
  const ctx = useContext(SurveyRuntimeAuthContext);
  if (!ctx) {
    throw new Error('useSurveyRuntimeAuth must be used within SurveyRuntimeAuthProvider');
  }
  return ctx;
}
